import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { AdminUser, AdminUserInput, AdminUserUpdate } from "@/types/rbac";

interface UsersResponse {
  users: AdminUser[];
}

interface UserResponse {
  user: AdminUser;
}

export function useAdminUsers(includeInactive = false) {
  return useQuery({
    queryKey: ["admin-users", includeInactive],
    queryFn: async () => {
      const params = includeInactive ? "?include_inactive=true" : "";
      const { users } = await apiRequest<UsersResponse>(`/api/admin/users${params}`);
      return users;
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin-user", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { user } = await apiRequest<UserResponse>(`/api/admin/users/${userId}`);
      return user;
    },
    enabled: !!userId,
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminUserInput) => {
      const { user } = await apiRequest<UserResponse>("/api/admin/users", {
        method: "POST",
        body: JSON.stringify(input),
      });
      return user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: AdminUserUpdate }) => {
      const { user } = await apiRequest<UserResponse>(`/api/admin/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      return user;
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user", user.userId] });
    },
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest(`/api/admin/users/${userId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      await apiRequest(`/api/admin/users/${userId}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ newPassword }),
      });
    },
  });
}
